import React from 'react';
import { graphql, Link } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import { Layout } from 'components/layout';
import {
  Affiliation, AlumniOf, GatsbyImageType, SocialMediaType,
} from 'types/common';
import { AuthorReviewerProducts } from 'templates/author-reviewer/author-reviewer-products';
import { AuthorReviewerSummary } from 'templates/author-reviewer/author-reviewer-summary';
import * as styles from 'templates/author-reviewer/author-reviewer.module.scss';
import { Button } from 'components/button';
import { ContentListNode } from 'components/article-list';
import { createAuthorsUrl, getFullName } from 'helpers/utils';
import { PageContext } from 'gatsby/internal';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs/breadcrumbs';
import { AuthorReviewerHeader } from 'templates/author-reviewer/author-reviewer-header';
import { AuthorReviewerLastSection } from 'templates/author-reviewer/author-reviewer-last-section';
import { ContributorTypeEnum } from 'utils/enums';
import { SEO } from 'components/seo';
import { BreadcrumbSEO, createBreadCrumbJSONLD, createPersonJSONLD } from 'helpers/seo-helpers';

type AuthorProps = {
  data: {
    allStrapiArticle: {
      nodes: ContentListNode[]
    }
    strapiAuthor: {
      nameFirst: string
      nameLast: string
      namePrefix: string
      nameSuffix: string
      contributorType: ContributorTypeEnum
      alumniOf: AlumniOf[]
      affiliation: Affiliation
      avatar: GatsbyImageType
      bio: string
      experience: {
        data: {
          experience: string
        }
      }
      expertise: string
      occupation: string
      shortDescription: string
      slug: string
      highlights: {
        value: string
      }[]
      education: {
        data: {
          education: string
        }
      }
      aboutTeethTalk?: {
        text: {
          data: {
            text: string
          }
        }
      }
      socialMedia: SocialMediaType
      components: {
        title: string
        image: GatsbyImageType
      }[]
    }
  },
  pageContext: PageContext & { numPages: number,
    perPage: number, skip: number, currentPage: number, subscribeBannerName: string, subscribeBannerLink: string }
};

const Author = ({ data, pageContext }: AuthorProps) => {
  const { strapiAuthor } = data;
  const fullName = getFullName(
    strapiAuthor.namePrefix,
    strapiAuthor.nameFirst,
    strapiAuthor.nameLast,
    strapiAuthor.nameSuffix,
  );

  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home page', slug: '/' },
    { name: 'Authors', slug: '/authors' },
    { name: fullName, slug: '' },
  ];

  return (
    <Layout>
      <div className={styles.genericContainer}>

        <Breadcrumbs breadcrumbs={breadcrumbs} className={styles.breadcrumb} />

        <AuthorReviewerHeader
          occupation={strapiAuthor.occupation}
          socialMedia={strapiAuthor.socialMedia}
          fullName={fullName}
          expertise={strapiAuthor.expertise}
          education={strapiAuthor.alumniOf}
          avatar={getImage(strapiAuthor.avatar.localFile)!}
          contributorType={strapiAuthor.contributorType}
        />
        {strapiAuthor.components && strapiAuthor.components.length > 0 && (
          <AuthorReviewerProducts products={strapiAuthor.components} />
        )}

        <AuthorReviewerSummary
          highlights={strapiAuthor.highlights}
          experience={strapiAuthor.experience.data.experience}
          education={strapiAuthor.education.data.education}
          aboutTeethTalk={strapiAuthor.aboutTeethTalk?.text.data.text || ''}
        />

        <div className={styles.buttonRow}>
          <Link to="/about-us">
            <Button bStyle="filled" bColor="tertiary" className={styles.learnMoreButton}>
              Learn about our editorial process
            </Button>
          </Link>
        </div>

        {data.allStrapiArticle?.nodes && data.allStrapiArticle?.nodes.length > 0 && (
          <AuthorReviewerLastSection
            numPages={pageContext.numPages}
            currentPage={pageContext.currentPage}
            articles={data.allStrapiArticle.nodes}
            fullName={fullName}
            urlSubscribe={pageContext.subscribeBannerLink}
            nameLink={pageContext.subscribeBannerName}
            
          />
        )}

      </div>
    </Layout>
  );
};

export const query = graphql`
  query($slug: String, $perPage: Int, $skip: Int) {
    allStrapiArticle(
    filter: {author: {slug: {eq: $slug}}}
    skip: $skip
    limit: $perPage
    ) {
    nodes {
      title
      slug
      description
      primaryImage {
        localFile {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
      category {
        slug
      }
      subcategory {
        slug
      }
    }
  }
    strapiAuthor(slug: {eq: $slug}) {
      nameFirst
      nameLast
      namePrefix
      nameSuffix
      contributorType
      slug
      shortDescription
      alumniOf {
        name
        url
      }
      affiliation {
        name
        url
      }
      avatar {
        url
        localFile {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
      bio
      experience {
        data {
          experience
        }
      }
      expertise
      occupation
      highlights {
        ... on STRAPI__COMPONENT_SCHEMA_SUBCOMPONENTS_DESCRIPTION {
          value
        }
      }
      education {
        data {
          education
        }
      }
      aboutTeethTalk {
        text {
          data {
            text
          }
        }
      }
      socialMedia {
        instagram
        youtube
        facebook
      }
      components {
        title
        image {
          localFile {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED)
            }
          }
        }
      }
    }
  }
`;

export default Author;

export const Head = ({ data, pageContext }: AuthorProps) => {
  const { strapiAuthor } = data;
  const fullName = getFullName(
    strapiAuthor.namePrefix,
    strapiAuthor.nameFirst,
    strapiAuthor.nameLast,
    strapiAuthor.nameSuffix,
  );

  const breadcrumbs: BreadcrumbSEO[] = [
    {
      position: 1,
      name: 'Teeth Talk Girl',
      slug: '',
    },
    {
      position: 2,
      name: 'All authors',
      slug: '/authors',
    },
    {
      position: 3,
      name: fullName,
      slug: createAuthorsUrl(strapiAuthor.slug),
    },
  ];

  const jsonLDArray = [
    JSON.stringify(createPersonJSONLD(
      fullName,
      createAuthorsUrl(strapiAuthor.slug),
      strapiAuthor.socialMedia,
      strapiAuthor.avatar.url,
      strapiAuthor.occupation,
    )),
    createBreadCrumbJSONLD(breadcrumbs),
  ];

  return (
    <SEO
      jsonLDArray={jsonLDArray}
      description={strapiAuthor.shortDescription}
      title={fullName}
      ogImageUrl={strapiAuthor.avatar.url}
      ogSlug={`/authors/${strapiAuthor.slug}${pageContext.currentPage !== 1 ? `/${pageContext.currentPage + 1}` : '/'}`}
    />
  );
};
